'use client'

import { Button } from '@/components/ui/button'
import { useScreenContext } from '@/features/layout/screen-context'
import { useSidebarContext } from '@/features/sidebar/sidebar-context'
import { cn } from '@/utils/styles/cn'
import { IChildrenProps } from '@/utils/typings/children-props'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import { ArrowLeftIcon, Menu } from 'lucide-react'
import { ReactNode } from 'react'

/** The page surface tint — exported so pinned chrome (filter bars) can match the header. */
export const PAGE_SURFACE_BACKGROUND_CLASS_NAME = 'bg-off-white'

export type IPageContainerProps = IChildrenProps &
    IClassNameProps & {
        style?: React.CSSProperties
        variant?: 'page' | 'insetCard'
        title?: string | ReactNode
        actionNodes?: ReactNode[]
        scrollable?: boolean
        childrenClassName?: string
        scrollContainerRef?: React.RefObject<HTMLDivElement | null>
        onBack?: () => void
        titleInlineStartNode?: ReactNode
        titleContainerClassName?: string
    }

export const PageContainer = ({
    children,
    className,
    style,
    variant = 'page',
    title,
    actionNodes,
    scrollable = true,
    childrenClassName,
    scrollContainerRef,
    onBack,
    titleInlineStartNode,
    titleContainerClassName,
}: IPageContainerProps) => {
    const { isMobile } = useScreenContext()
    const { setMobileSidebarOpen } = useSidebarContext()

    const topRowHeight = isMobile ? 52 : 56
    const hasActionNodes = Boolean(actionNodes?.length)
    const showTitleBar = Boolean(onBack || title || titleInlineStartNode || isMobile || hasActionNodes)

    const wrapperClassName = cn(
        'relative flex flex-col flex-1 min-h-0 h-full w-full',
        variant === 'page' ? 'max-h-[100dvh]' : 'max-h-[calc(100dvh-16px)] pb-4'
    )

    const innerSurfaceClassName = cn(
        'min-h-full h-full max-h-full w-full flex relative flex-col flex-1 overflow-hidden',
        PAGE_SURFACE_BACKGROUND_CLASS_NAME,
        variant === 'page' ? !isMobile && 'rounded-[16px] border-[0.5px] border-linen' : 'rounded-[12px]',
        className
    )

    const contentClassName = cn(
        'flex-1 min-h-0 min-w-full w-full pl-4 pr-[10px] py-4 pb-0 flex flex-col',
        scrollable ? 'overflow-y-auto good-scrollbar-with-padding' : 'overflow-y-hidden',
        childrenClassName
    )

    return (
        <div className={wrapperClassName}>
            <div className={innerSurfaceClassName} style={style}>
                {showTitleBar && (
                    <div
                        className={cn(
                            'w-full flex items-center gap-2 z-20 md:relative sticky top-0 border-b border-black/5',
                            PAGE_SURFACE_BACKGROUND_CLASS_NAME,
                            isMobile ? 'pl-2 pr-3 h-[52px]' : 'px-4 h-[56px]',
                            titleContainerClassName
                        )}
                    >
                        {isMobile && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setMobileSidebarOpen(true)}
                                className="hover:bg-black/5 transition-none shrink-0"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        )}

                        {onBack && (
                            // 16px arrow, muted — the board detail's in-title back arrow is the
                            // reference; the Button default would inflate the svg to 18px.
                            <Button
                                variant="ghost"
                                size="icon"
                                className="[--btn-icon-size:16px] text-muted-foreground hover:text-foreground"
                                onClick={onBack}
                            >
                                <ArrowLeftIcon className="h-4 w-4" />
                            </Button>
                        )}

                        <div
                            className="flex items-center w-full justify-between gap-2 min-w-0"
                            style={{
                                minHeight: topRowHeight,
                                maxHeight: topRowHeight,
                            }}
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                {title &&
                                    (typeof title === 'string' ? (
                                        <div className="shrink-0 truncate text-[16px] font-semibold leading-5 tracking-[-0.02em] text-[#131210]">
                                            {title}
                                        </div>
                                    ) : (
                                        title
                                    ))}

                                {titleInlineStartNode}
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                                {hasActionNodes && (
                                    <div className="flex items-center gap-3">{actionNodes?.map((node) => node)}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className={contentClassName} ref={scrollContainerRef}>
                    {children}
                </div>
            </div>
        </div>
    )
}
